import * as React from 'react';
import { useEffect, useState } from 'react';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Box from "@mui/joy/Box";
import Checkbox from '@mui/joy/Checkbox';
import Chip from '@mui/joy/Chip';
import { CircularProgress, IconButton } from "@mui/joy";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import GeneralApiService from "../../axios/Axios";
import {toast} from "react-toastify";
import ResponsiveTableWrapper from "../ui/ResponsiveTableWrapper";

interface Voicemail {
    id: number;
    created_at: string;
    customer_name: string;
    phone_number: string;
    recording_url: string;
    duration: number;
    listened: boolean;
    call_category?: string;
}

function formatDate(old_date: string | Date): string {
    const date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-GB', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }
    return 'No Valid Date';
}

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

function VoicemailRow({ row, onListen }: { row: Voicemail, onListen: (id: number) => void }) {
    const audioRef = React.useRef<HTMLAudioElement | null>(null);

    return (
        <tr>
            <td>{formatDate(row.created_at)}</td>
            <td>
                <Typography level="body-sm">{row.customer_name}</Typography>
                <Typography level="body-xs">{row.phone_number}</Typography>
            </td>
            <td>
                <Chip size="sm" variant="outlined" color={row.call_category === 'Existing Booking' ? 'success' : 'neutral'}>
                    {row.call_category || 'Sales'}
                </Chip>
            </td>
            <td>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <audio 
                        ref={audioRef} 
                        src={row.recording_url} 
                        controls 
                        preload="none"
                        onPlay={() => {
                            if (!row.listened) {
                                onListen(row.id);
                            }
                        }}
                    />
                </Box>
            </td>
            <td>
                <Checkbox 
                    checked={row.listened} 
                    label="Listened" 
                    onChange={() => !row.listened && onListen(row.id)}
                    disabled={row.listened} 
                />
            </td>
        </tr>
    );
}

export default function VoicemailTable({ search, idFilter, category }: { search?: string, idFilter?: string | null, category?: string }) {
    const [rows, setRows] = useState<Voicemail[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);

    const getLabelDisplayedRowsTo = () => Math.min(pageCount, (page + 1) * rowsPerPage);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params: any = { page: page + 1 };
            
            // Get current location from localStorage and pass to API
            const currentLocation = localStorage.getItem('location');
            if (currentLocation) {
                params.location = currentLocation.toLowerCase().replace(/ /g, '_');
            }
            
            if (idFilter) {
                params.id = idFilter;
            } else if (search) {
                params.search = search;
            }
            
            if (category && category !== 'All') {
                params.category = category;
            }

            const data = await GeneralApiService.getVoicemails(params);
            
            // Check if data is paginated (has results/count) or direct array
            if (data.results && typeof data.count === 'number') {
                setRows(data.results);
                setPageCount(data.count);
            } else {
                setRows(data);
                setPageCount(data.length);
            }
        } catch (error) {
            console.error("Failed to fetch voicemails:", error);
            toast.error("Failed to load voicemails");
            setRows([]);
            setPageCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData();
        }, 300); // 300ms debounce

        return () => clearTimeout(handler);
    }, [search, idFilter, page, category]);

    const handleListen = async (id: number) => {
        try {
            await GeneralApiService.markVoicemailListened(id);
            // Optimistic update
            setRows(prev => prev.map(r => r.id === id ? { ...r, listened: true } : r));
        } catch (error) {
            console.error("Failed to mark listened:", error);
        }
    };

    return (
        <ResponsiveTableWrapper minWidth={700}>
            <Table hoverRow sx={{ mt: 2 }}>
                <thead>
                <tr>
                    <th style={{ width: '20%' }}>Date</th>
                    <th style={{ width: '25%' }}>Customer</th>
                    <th style={{ width: '15%' }}>Category</th>
                    <th style={{ width: '25%' }}>Recording</th>
                    <th style={{ width: '15%' }}>Status</th>
                </tr>
                </thead>
                <tbody>
                {loading ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                ) : rows.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>No voicemails found</td></tr>
                ) : (
                    rows.map((row) => (
                        <VoicemailRow key={row.id} row={row} onListen={handleListen} />
                    ))
                )}
                </tbody>
                <tfoot>
                <tr>
                    <td colSpan={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                            <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                {labelDisplayedRows({ 
                                    from: pageCount === 0 ? 0 : page * rowsPerPage + 1, 
                                    to: getLabelDisplayedRowsTo(), 
                                    count: pageCount 
                                })}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton 
                                    size="sm" 
                                    color="neutral" 
                                    variant="outlined" 
                                    disabled={page === 0} 
                                    onClick={() => setPage(page - 1)} 
                                    sx={{ bgcolor: 'background.surface' }}
                                >
                                    <KeyboardArrowLeft />
                                </IconButton>
                                <IconButton 
                                    size="sm" 
                                    color="neutral" 
                                    variant="outlined" 
                                    disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} 
                                    onClick={() => setPage(page + 1)} 
                                    sx={{ bgcolor: 'background.surface' }}
                                >
                                    <KeyboardArrowRight />
                                </IconButton>
                            </Box>
                        </Box>
                    </td>
                </tr>
                </tfoot>
            </Table>
        </ResponsiveTableWrapper>
    );
}

